import * as React from 'react';

import { Button } from '@rematter/ui-web';

import { useTranslations } from '@/finance/i18n/useTranslations';
import { financeRoutes } from '~/modules/finance/routes';
import { LinkedBillRecord, LinkedInvoiceRecord } from '~/modules/finance/types';

type LinkedDoc = {
  id: string;
  documentNumber: string;
  type: 'bill' | 'invoice';
};

type Props = {
  linkedBills: LinkedBillRecord[];
  linkedInvoices: LinkedInvoiceRecord[];
};

export const PaymentLinkedDocuments = ({
  linkedBills,
  linkedInvoices,
}: Props) => {
  const t = useTranslations();

  const docs: LinkedDoc[] = React.useMemo(
    () => [
      ...linkedBills.map((b) => ({
        id: b.id,
        documentNumber: b.documentNumber,
        type: 'bill' as const,
      })),
      ...linkedInvoices.map((inv) => ({
        id: inv.id,
        documentNumber: inv.documentNumber,
        type: 'invoice' as const,
      })),
    ],
    [linkedBills, linkedInvoices],
  );

  const handleDocClick = React.useCallback((doc: LinkedDoc) => {
    const route =
      doc.type === 'bill'
        ? financeRoutes.billDetail.replace(':id', doc.id)
        : financeRoutes.invoiceDetail.replace(':id', doc.id);
    window.open(route, '_blank', 'noopener,noreferrer');
  }, []);

  if (docs.length === 0) return <>-</>;

  const visible = docs.slice(0, 3);
  const remaining = docs.length - 3;

  return (
    <span className="@PaymentLinkedDocuments flex items-center gap-1 truncate">
      {visible.map((doc, index) => (
        <span key={doc.id} onClick={(e) => e.stopPropagation()}>
          <Button variant="link" onPress={() => handleDocClick(doc)}>
            {doc.documentNumber}
          </Button>
          {index < visible.length - 1 && ', '}
        </span>
      ))}
      {remaining > 0 && (
        <span className="ml-1 text-xs text-foreground/50">
          {t.payments.list.columns.linkedDocumentsMore({
            count: remaining,
          })}
        </span>
      )}
    </span>
  );
};
